import { Location } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

/** Disables browser Back and Forward for in-app routes without stacking dummy history. */
@Injectable({ providedIn: 'root' })
export class BrowserHistoryLockService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private lockedRouterUrl = '';
  private popstateNavigation = false;
  private ignoreNextPop = false;

  constructor() {
    this.lockedRouterUrl = this.router.navigated ? this.router.url : this.browserRouterUrl();

    const onPopState = (): void => {
      if (this.ignoreNextPop) {
        this.ignoreNextPop = false;
        return;
      }
      this.ignoreNextPop = true;
      history.go(1);
      if (this.lockedRouterUrl) {
        void this.router.navigateByUrl(this.lockedRouterUrl, { replaceUrl: true });
      }
    };

    window.addEventListener('popstate', onPopState, true);
    inject(DestroyRef).onDestroy(() => window.removeEventListener('popstate', onPopState, true));

    this.stamp();

    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart && event.navigationTrigger === 'popstate') {
        this.popstateNavigation = true;
        if (this.lockedRouterUrl && !this.sameRouterUrl(event.url, this.lockedRouterUrl)) {
          void this.router.navigateByUrl(this.lockedRouterUrl, { replaceUrl: true });
        }
        return;
      }

      if (!(event instanceof NavigationEnd)) {
        return;
      }

      if (this.popstateNavigation) {
        this.popstateNavigation = false;
        this.stamp();
        return;
      }

      this.lockedRouterUrl = event.urlAfterRedirects;
      this.stamp();
    });
  }

  /** Update the current history entry in place — do not push extras Chrome can list. */
  private stamp(): void {
    const href = this.lockedRouterUrl
      ? this.location.prepareExternalUrl(this.lockedRouterUrl)
      : this.browserHref();
    history.replaceState(history.state, '', href);
  }

  private browserHref(): string {
    return `${location.pathname}${location.search}${location.hash}`;
  }

  private browserRouterUrl(): string {
    return `${location.pathname}${location.search}${location.hash}` || '/';
  }

  private sameRouterUrl(left: string, right: string): boolean {
    return left.split('?')[0] === right.split('?')[0];
  }
}
